import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Search, Loader2, FileText, Hash, SlidersHorizontal, X } from "lucide-react";
import Layout from "../components/layout/Layout";
import ArticleCard from "../components/ui/ArticleCard";
import { useArticles } from "../hooks/useArticles";

export default function ArticlesPage() {
  const [searchParams] = useSearchParams();
  const initialTag = searchParams.get("tag") || "";
  const { articles, loading } = useArticles();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState(initialTag);
  const [selectedSpecialization, setSelectedSpecialization] = useState("");

  const allTags = useMemo(() => {
    const tagSet = new Set();
    articles.forEach((a) => (a.tags || []).forEach((t) => tagSet.add(t)));
    return [...tagSet].sort();
  }, [articles]);

  const allSpecializations = useMemo(() => {
    const specSet = new Set();
    articles.forEach((a) => {
      (a.author?.specializations || []).forEach((s) => specSet.add(s));
    });
    return [...specSet].sort();
  }, [articles]);

  const filteredArticles = useMemo(() => {
    let result = [...articles];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          (a.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }

    if (selectedTag) {
      result = result.filter((a) => (a.tags || []).includes(selectedTag));
    }

    if (selectedSpecialization) {
      result = result.filter((a) =>
        (a.author?.specializations || []).includes(selectedSpecialization)
      );
    }

    return result;
  }, [articles, searchQuery, selectedTag, selectedSpecialization]);

  const hasActiveFilters = selectedTag || selectedSpecialization;

  return (
    <Layout>
      <div id="articles_page" className="min-h-screen">
        <section className="py-12 lg:py-16 border-b border-neutral-800/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-2"
            >
              Статьи экспертов
            </motion.h1>
            <p className="text-xl font-extralight text-neutral-400 mb-3 leading-relaxed max-w-3xl">
              Обстучим — сообщество экспертов и практиков из реального бизнеса.
              Здесь вы найдёте проверенные знания от людей, которые сами прошли через задачи,
              похожие на ваши.
            </p>
            <p className="text-lg font-extralight text-neutral-500 mb-8">
              Статьи написаны экспертами платформы — вы можете записаться к любому из них на консультацию
            </p>

            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
              <input
                type="text"
                placeholder="Поиск по статьям..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-900/60 border border-neutral-800/50 text-neutral-100 text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
              />
            </div>

            {allSpecializations.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <SlidersHorizontal className="w-4 h-4 text-neutral-500" />
                  <span className="text-lg font-extralight text-neutral-500">Тематика эксперта</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSpecialization("")}
                    className={`px-3 py-1 rounded-md text-lg font-extralight transition-colors ${
                      !selectedSpecialization
                        ? "bg-accent-600 text-white"
                        : "bg-neutral-800/40 text-neutral-400 hover:text-white"
                    }`}
                  >
                    Все тематики
                  </button>
                  {allSpecializations.map((spec) => (
                    <button
                      key={spec}
                      onClick={() => setSelectedSpecialization(selectedSpecialization === spec ? "" : spec)}
                      className={`px-3 py-1 rounded-md text-lg font-extralight transition-colors ${
                        selectedSpecialization === spec
                          ? "bg-accent-600 text-white"
                          : "bg-neutral-800/40 text-neutral-400 hover:text-white"
                      }`}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {allTags.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-neutral-500" />
                  <span className="text-lg font-extralight text-neutral-500">Теги</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTag("")}
                    className={`px-3 py-1 rounded-md text-lg font-extralight transition-colors ${
                      !selectedTag
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-800/40 text-neutral-400 hover:text-white"
                    }`}
                  >
                    Все теги
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(selectedTag === tag ? "" : tag)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-lg font-extralight transition-colors ${
                        selectedTag === tag
                          ? "bg-primary-600 text-white"
                          : "bg-neutral-800/40 text-neutral-400 hover:text-white"
                      }`}
                    >
                      <Hash className="w-3.5 h-3.5" />
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <div className="mt-4 flex items-center gap-3">
                <span className="text-lg font-extralight text-neutral-500">
                  Найдено: {filteredArticles.length}
                </span>
                <button
                  onClick={() => { setSelectedTag(""); setSelectedSpecialization(""); }}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-neutral-800/40 text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Сбросить фильтры
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-8 lg:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
                <p className="text-lg font-extralight text-neutral-500">Загрузка статей...</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
                <h3 className="text-xl font-light text-neutral-200 mb-2">
                  {articles.length === 0 ? "Пока нет статей" : "Ничего не найдено"}
                </h3>
                <p className="text-lg font-extralight text-neutral-500">
                  {articles.length === 0
                    ? "Эксперты скоро начнут публиковать статьи"
                    : "Попробуйте изменить запрос или выбрать другой тег"}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
